import {
  type Alert,
  type AlertSeverity,
  AlertSeveritySchema,
  type AlertType,
  AlertTypeSchema,
  type BunkStatus,
  BunkStatusSchema,
  type EventType,
  EventTypeSchema,
  type FeedingGroup,
  FeedingGroupSchema,
  type ViewType,
  ViewTypeSchema,
} from '@shared/enums';
import { z } from 'zod';

// --- Re-export shared enums for use across the web app ---

export {
  FeedingGroupSchema,
  BunkStatusSchema,
  EventTypeSchema,
  AlertTypeSchema,
  AlertSeveritySchema,
  ViewTypeSchema,
};
export type { FeedingGroup, BunkStatus, EventType, AlertType, AlertSeverity, Alert, ViewType };

export type Role = 'farmer' | 'helper' | 'viewer';

// --- Log Feeding ---

export const LogFeedingFormSchema = z.object({
  date: z.string().date({ error: 'Date is required' }),
  time_delivered: z.string().min(1, { error: 'Time is required' }),
  group: FeedingGroupSchema,
  head_count: z.coerce.number().int().positive({ error: 'Must be a positive number' }),
  batch_hours: z.coerce.number().positive({ error: 'Must be a positive number' }),
  end_weight_lbs: z.coerce
    .number()
    .positive({ error: 'Must be a positive number' })
    .optional()
    .or(z.literal('')),
  bunk_status: BunkStatusSchema.optional(),
  notes: z.string().optional(),
});

export type LogFeedingFormData = z.infer<typeof LogFeedingFormSchema>;

// --- API Response types ---

export interface LogFeedingResponse {
  status: 'ok';
  feeding_id: string;
  computed: {
    lbs_per_head: number | null;
    vs_7day_avg: number | null;
  };
}

export interface SessionResponse {
  email: string;
  role: Role;
}

// --- Dashboard Summary ---

export interface DashboardSummary {
  as_of: string;
  herd: {
    total: number;
    milking: number;
    dry: number;
    close_up: number;
    heifers: number;
  };
  today_feeding: {
    logged: boolean;
    end_weight_lbs: number | null;
    lbs_per_head: number | null;
    bunk_status: string | null;
    logged_at: string | null;
  } | null;
  inventory_top_concerns: Array<{
    name: string;
    days_remaining: number;
    below_threshold: boolean;
  }>;
  alerts: Alert[];
  recent_activity: Array<{
    timestamp: string;
    description: string;
  }>;
}

// --- Herd types ---

export type AnimalStatus = 'Milking' | 'Dry' | 'Close-up' | 'Heifer' | 'Calf' | 'Dead' | 'Sold';

export interface HerdAnimal {
  animal_id: string;
  name: string | null;
  status: string;
  barn_group: string | null;
  birth_date: string | null;
  lactation_number: number | null;
  dim: number | null;
  expected_calving_date: string | null;
  days_until_calving: number | null;
  preg_status: string | null;
  last_breeding_date: string | null;
  sire: string | null;
  notes: string | null;
}

export interface HerdListResponse {
  animals: HerdAnimal[];
  total: number;
}

// --- Ration types ---

export interface Ingredient {
  name: string;
  item_id: string | null;
  lbs_per_head_day: number;
  dm_pct: number | null;
}

export interface Ration {
  ration_id: string;
  group_id: string;
  effective_date: string;
  active: boolean;
  head_count_at_creation: number | null;
  batch_hours: number;
  ingredients: Ingredient[];
  total_lbs_per_head: number | null;
  total_batch_weight_lbs: number | null;
  notes: string | null;
}

export interface CurrentRationsResponse {
  rations: Ration[];
}

// --- Event types ---

export const EVENT_TYPE_LABELS: Record<EventType, string> = {
  calving: 'Calving',
  breeding: 'Breeding',
  preg_check: 'Preg Check',
  dry_off: 'Dry-Off',
  health: 'Health',
  death: 'Death',
  sold: 'Sold',
  group_change: 'Group Change',
  note: 'Note',
};

export const AddEventFormSchema = z.object({
  animal_id: z.string().min(1, { error: 'Animal ID is required' }),
  event_type: EventTypeSchema,
  event_date: z.string().optional(),
  event_time: z.string().optional(),
  result: z.string().optional(),
  calf_id: z.string().optional(),
  calf_sex: z.enum(['heifer', 'bull', 'stillbirth']).optional(),
  ease_of_birth: z.enum(['unassisted', 'easy_pull', 'hard_pull', 'surgical']).optional(),
  sire: z.string().optional(),
  to_group: z.string().optional(),
  notes: z.string().optional(),
});
export type AddEventFormData = z.infer<typeof AddEventFormSchema>;

export interface AddEventResponse {
  event_id: string;
  audit_id: string;
  herd_register_updates: Record<string, unknown>;
}

// --- Alert response ---

export interface AlertsResponse {
  alerts: Alert[];
}

// --- Animal detail (for slide-in panel) ---

export interface EventSummary {
  event_id: string;
  animal_id: string;
  event_type: string;
  event_date: string;
  event_time: string | null;
  result: string | null;
  calf_id: string | null;
  calf_sex: string | null;
  sire: string | null;
  notes: string | null;
  logged_by: string;
}

export interface AnimalDetail extends HerdAnimal {
  dam_id: string | null;
  expected_dry_date: string | null;
  created_at: string;
  updated_at: string;
  events: EventSummary[];
}

// --- Inventory ---

export interface InventoryItem {
  item_id: string;
  name: string;
  category: string;
  quantity_on_hand: number;
  unit: string;
  price_per_unit: number | null;
  total_value: number | null;
  daily_usage_rate: number | null;
  daily_usage_lbs_per_head: number | null;
  days_remaining: number | null;
  date_empty_projected: string | null;
  alert_threshold_days: number;
  below_threshold: boolean;
  storage_notes: string | null;
  active: boolean;
}

export interface QueryInventoryResult {
  items: InventoryItem[];
  total_value: number;
}

export type QueryInventoryParams = {
  category?: string;
  low_stock_only?: boolean;
  include_inactive?: boolean;
  sort_by?: 'days_remaining' | 'name' | 'category' | 'value';
};

// --- Audit log ---

export interface AuditEntry {
  audit_id: string;
  timestamp: string;
  actor: string;
  user_email: string;
  user_role: string;
  tool: string;
  action: string;
  target_sheet: string;
  target_id: string | null;
  before_snapshot: Record<string, unknown> | null;
  after_snapshot: Record<string, unknown> | null;
  context: string | null;
  risk_level: string;
  rolled_back: boolean;
  rollback_of: string | null;
}

export interface AuditLogResponse {
  entries: AuditEntry[];
  total: number;
}

export type AuditQueryParams = {
  actor?: string;
  target_sheet?: string;
  date_from?: string;
  date_to?: string;
  limit?: number;
  offset?: number;
};

// --- Config ---

export interface ConfigEntry {
  key: string;
  value: string;
  description: string;
  updated_at: string;
}

// --- Events query response ---

export interface EventsResponse {
  events: EventSummary[];
  total: number;
}

export type EventQueryParams = {
  animal_id?: string;
  event_type?: string;
  date_from?: string;
  date_to?: string;
  limit?: number;
  offset?: number;
};

// --- View types ---

export interface ViewSummary {
  view_id: string;
  title: string;
  type: string;
  pinned: boolean;
  pin_order: number | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface ViewListResponse {
  views: ViewSummary[];
}

export interface ViewDataResponse {
  rows: Record<string, unknown>[];
  executed_at: string;
}

export interface ViewChartConfig {
  x?: string;
  y?: Array<{ field: string; label: string; color?: string }>;
  annotations?: Array<{ x: string; label: string; style?: string }>;
  show_trend_line?: boolean;
  [key: string]: unknown;
}

export interface ViewDetail {
  view_id: string;
  title: string;
  type: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  pinned: boolean;
  pin_order: number | null;
  refresh_interval_minutes: number | null;
  data_query?: Record<string, unknown>;
  data?: unknown[];
  chart_config: ViewChartConfig;
}
